import { useEffect, useState } from "react";
import { BarChart3 } from "lucide-react";
import { eventsApi } from "@/services/eventsApi";

const statDefs = [
  ["page_view", "Page views"],
  ["quote_open", "Quote opened"],
  ["quote_submit", "Quotes submitted"],
  ["whatsapp_click", "WhatsApp clicks"],
  ["brochure_view", "Brochure views"],
  ["gear_enquiry", "Gear enquiries"],
];

const gearLabel = (page) => page.replace("/gear/", "").split("-").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");

const pageLabel = (page) => ({ "/": "Home", "/production": "Production", "/rnd": "R&D", "/store": "Gear", "/events": "Events" }[page] || page);

export const InsightsPanel = () => {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  useEffect(() => { eventsApi.get("/admin/insights").then(({ data }) => { setData(data); setState("ready"); }).catch(() => setState("error")); }, []);
  if (state === "loading") return <section className="admin-panel"><div className="admin-panel-empty" data-testid="insights-loading"><BarChart3/><p>Loading conversion insights...</p></div></section>;
  if (state === "error") return <section className="admin-panel"><div className="admin-panel-empty" data-testid="insights-error"><BarChart3/><p>Insights are temporarily unavailable.</p></div></section>;
  const totals = data.totals || {};
  const maxDaily = Math.max(1, ...(data.daily_enquiries || []).map((d) => d.count));
  const gearRows = (data.pages || []).filter((row) => row.page.startsWith("/gear/") && row.gear_enquiry).sort((a, b) => (b.gear_enquiry || 0) - (a.gear_enquiry || 0));
  const pageRows = (data.pages || []).filter((row) => !row.page.startsWith("/gear/"));
  return (
    <section className="admin-panel" data-testid="admin-insights-panel">
      <div className="admin-panel-heading"><div><p className="eyebrow">Last {data.window_days} days</p><h2>CONVERSION INSIGHTS</h2></div><p>Privacy-friendly counts only — no cookies, no visitor identity stored.</p></div>
      <div className="insights-stat-row">
        {statDefs.map(([key, label]) => <div key={key} className="insight-stat" data-testid={`insight-stat-${key}`}><strong>{totals[key] || 0}</strong><span>{label}</span></div>)}
      </div>
      {(data.daily_enquiries || []).length > 0 && (
        <div className="insights-trend" data-testid="insights-trend">
          <p className="eyebrow">Daily enquiries (quote submits + WhatsApp)</p>
          <div className="trend-bars">{data.daily_enquiries.map((d) => <div key={d.day} className="trend-bar" title={`${d.day}: ${d.count}`}><span style={{ height: `${Math.max(8, (d.count / maxDaily) * 100)}%` }}/><small>{d.day.slice(8)}</small></div>)}</div>
        </div>
      )}
      {gearRows.length > 0 && (
        <div className="insights-gear" data-testid="insights-gear-section">
          <p className="eyebrow">Top gear enquiries</p>
          <div className="gear-rank-list">
            {gearRows.map((row, i) => (
              <div key={row.page} className="gear-rank-row" data-testid={`insights-gear-${i + 1}`}>
                <span className="gear-rank-index">{String(i + 1).padStart(2, "0")}</span>
                <strong>{gearLabel(row.page)}</strong>
                <span className="gear-rank-bar"><i style={{ width: `${(row.gear_enquiry / (gearRows[0].gear_enquiry || 1)) * 100}%` }}/></span>
                <em>{row.gear_enquiry}</em>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="insights-table-wrap">
        <p className="eyebrow">Performance by page</p>
        <table className="insights-table" data-testid="insights-page-table">
          <thead><tr><th>Page</th><th>Views</th><th>Quote opens</th><th>Quote submits</th><th>WhatsApp</th><th>Conversion</th></tr></thead>
          <tbody>
            {pageRows.length === 0 && <tr><td colSpan="6" className="insights-empty-row" data-testid="insights-empty">No activity recorded yet. Data appears as visitors browse the site.</td></tr>}
            {pageRows.map((row) => {
              const views = row.page_view || 0;
              const conversions = (row.quote_submit || 0) + (row.whatsapp_click || 0);
              return (
                <tr key={row.page} data-testid={`insights-row-${row.page.replace(/\//g, "") || "home"}`}>
                  <td><strong>{pageLabel(row.page)}</strong><small>{row.page}</small></td>
                  <td>{views}</td>
                  <td>{row.quote_open || 0}</td>
                  <td>{row.quote_submit || 0}</td>
                  <td>{row.whatsapp_click || 0}</td>
                  <td>{views ? `${Math.round((conversions / views) * 100)}%` : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};
